/**
 * Analytics Portal V1 — Private Browsing (Phase 17, plan §8.5, §10.6, §11 endpoints 14–15).
 *
 * An ACCOUNT-level preference, so it lives in Account Settings and nowhere else — never inside a
 * Profile, Business, Capital, Event or Resource dashboard, because it is not an entity permission.
 * It saves itself immediately through `PUT /api/prefs/analytics` (the one approved preference
 * endpoint) rather than riding the page's Save button, so the state on screen is always the state
 * on the server. Nothing is persisted in the browser: the value is re-read from the server on every
 * mount.
 *
 * The copy is deliberately narrow. Private Browsing hides the member's identity from Analytics
 * named-visitor lists only — it is not platform-wide anonymity, it does not touch messages,
 * comments, follows or questions, it does not affect operational security logs, and it is never
 * retroactive in either direction.
 */
import React, { useEffect, useState } from "react";
import { EyeOff, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

export const PrivateBrowsingCard = () => {
  const [state, setState] = useState({ status: "loading", on: false, available: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    api.get("/prefs/analytics")
      .then(({ data }) => live && setState({
        status: "ready", on: Boolean(data?.private_browsing),
        available: Boolean(data?.private_browsing_available),
      }))
      .catch(() => live && setState({ status: "ready", on: false, available: false }));
    return () => { live = false; };
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await api.put("/prefs/analytics", { private_browsing: next });
      setState((prev) => ({ ...prev, on: Boolean(data?.private_browsing) }));
      setSaved(true);
      toast.success(next ? "Private Browsing is on" : "Private Browsing is off");
    } catch {
      toast.error("We couldn't save that setting. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (state.status === "loading") {
    return (
      <Card className="p-6" data-testid="private-browsing-card">
        <h2 className="text-lg font-[640] text-foreground">Private Browsing</h2>
        <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground" role="status"
           data-testid="private-browsing-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading your setting…
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6" data-testid="private-browsing-card">
      <h2 className="flex items-center gap-2 text-lg font-[640] text-foreground">
        <EyeOff className="h-4 w-4 text-primary" aria-hidden="true" /> Private Browsing
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-[520] text-muted-foreground"
              data-testid="private-browsing-tier-badge">CAPITAL</span>
      </h2>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="private-browsing-copy">
        When Private Browsing is on, your visits still count in aggregate Analytics, but you will not
        appear by name in Named Visitors. The setting applies to future visits until you turn it off.
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        It changes Analytics visitor identification only. Your previous named visits stay as they were
        recorded, and messages, comments, follows and questions are unaffected — those remain visible
        to the people you send them to.
      </p>

      {state.available ? (
        <>
          <div className="mt-4 flex items-center justify-between gap-4">
            <Label htmlFor="private-browsing-switch" className="font-[440] text-foreground">
              Hide my name from Analytics visitor lists
            </Label>
            <Switch id="private-browsing-switch" checked={state.on} disabled={saving}
                    onCheckedChange={toggle}
                    aria-describedby="private-browsing-status"
                    data-testid="analytics-private-browsing-toggle" />
          </div>
          <p id="private-browsing-status" className="mt-2 text-xs" role="status"
             data-testid="private-browsing-status">
            {saving ? (
              <span className="text-muted-foreground">Saving…</span>
            ) : (
              <span className={state.on ? "text-teal-700" : "text-muted-foreground"}>
                {state.on ? "On — you are hidden from Named Visitors on future visits."
                          : "Off — profile managers can see your name after you visit."}
                {saved ? " Saved." : ""}
              </span>
            )}
          </p>
        </>
      ) : state.on ? (
        // Not entitled, but already hidden: never silently expose them. They keep the protection and
        // get one clear way to end it — and once it is off, enabling again needs CAPITAL.
        <div className="mt-4 rounded-lg border border-border bg-secondary/60 px-3 py-3"
             data-testid="private-browsing-active-ineligible">
          <p className="text-sm text-teal-700" role="status" id="private-browsing-status"
             data-testid="private-browsing-status">
            On — you are currently hidden from Named Visitors on future visits.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Private Browsing is a CAPITAL feature. Your setting stays on until you turn it off, and
            turning it back on later needs CAPITAL.
          </p>
          <Button variant="secondary" size="sm" className="mt-3" disabled={saving}
                  onClick={() => toggle(false)}
                  data-testid="private-browsing-turn-off">
            {saving ? "Turning off…" : "Turn off Private Browsing"}
          </Button>
        </div>
      ) : (
        <p className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground"
           role="note" data-testid="private-browsing-upsell">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>Private Browsing is included with CAPITAL. Upgrade to browse profiles without
            appearing by name in their Analytics.</span>
        </p>
      )}
    </Card>
  );
};
